import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import {
	QuickInsertMenuItem,
	type OnSelectContext,
} from '@atlaskit/editor-common/quick-insert/menu-item';
import { useQuickInsertContext } from '@atlaskit/editor-common/quick-insert/use-quick-insert-context';
import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';

import type { AIPlugin } from '../../aiPluginType';
import type { AIGlobalOptIn } from '../../types/types';
import type { EditorPluginAIConfigItem } from '../prebuilt/config-items/config-items';
import { mapConfigItemsToQuickInsertItems } from '../prebuilt/config-items/utils/map-config-items-to-quick-insert-items';
import { useIsOffline } from '../toolbars/utils/use-is-offline';

type Props = {
	aiGlobalOptIn: AIGlobalOptIn;
	api: ExtractInjectionAPI<AIPlugin> | undefined;
	configItem: EditorPluginAIConfigItem;
};

export const AIQuickInsertMenuItem = ({
	aiGlobalOptIn,
	api,
	configItem,
}: Props): React.JSX.Element | null => {
	const intl = useIntl();
	const { editorView } = useQuickInsertContext();
	const item = useMemo(
		() =>
			mapConfigItemsToQuickInsertItems({
				aiGlobalOptIn,
				configItems: [configItem],
				editorView,
				formatMessage: intl.formatMessage,
				locale: intl.locale,
			}).at(0),
		[aiGlobalOptIn, configItem, editorView, intl.formatMessage, intl.locale],
	);
	const isOffline = useIsOffline(api);
	const onSelect = useCallback(
		({ editorView, insert, source }: OnSelectContext) =>
			item?.action(insert, editorView.state, source),
		[item],
	);

	if (!item) {
		return null;
	}

	return (
		<QuickInsertMenuItem
			iconBefore={item.icon?.()}
			isDisabled={isOffline}
			onSelect={onSelect}
			title={item.title}
		/>
	);
};
